import React, { type FC } from "react";
import { Flex, Text, Box, SimpleGrid, AspectRatio } from "@chakra-ui/react";
import { type GetStaticProps } from "next";
import NextImage from "next/legacy/image";
import {
  type EventsPageSimple,
  type ExternalEvent,
  type Page as SanityPage,
  getUpcomingExternalEvents,
  getPastExternalEvents,
  getPage,
  getUpcomingEventsPages,
  getPastEventsPages,
  getUpcomingEventSeries,
  getPastEventSeries,
  type EventSeriesSimple,
  type Banner,
  getAnnouncementBanner,
} from "lib/sanity";
import { Section } from "components/section";
import { Page } from "components/page";
import { SimpleTextHero } from "components/hero";
import { ResourceCard } from "components/resource-card";
import { eventNewsletterCta } from "components/events-newsletter-form";
import { CalendarIcon, PinIcon } from "ui/icons";
import { formatDateWithYear, getPageMetaImage } from "utils";
import { TextTitle } from "ui/text";

type EventCard = {
  _type: string;
  title: string;
  showInDirectory?: boolean;
  imageUrl: string;
  metaImageUrl?: string;
  location?: string;
  startDate: string;
  endDate?: string;
  excerpt?: string;
  registrationUrl?: string;
  slug?: string;
  isPast?: boolean;
};

type PageProps = {
  upcomingEvents: EventCard[];
  pastEvents: EventCard[];
  page: SanityPage;
  announcementBanner?: Banner;
};

const Events: FC<Readonly<PageProps>> = ({ page, announcementBanner, upcomingEvents, pastEvents }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      footerOptions={{
        ctaProps: eventNewsletterCta,
      }}
    >
      <SimpleTextHero
        title={upcomingEvents.length > 0 ? "Upcoming events" : page.title}
        description={page.description}
      />

      {upcomingEvents.length > 0 && (
        <Section my={0}>
          <SimpleGrid as="ol" listStyleType="none" columns={{ base: 1, md: 2, lg: 3 }} gap={10}>
            {upcomingEvents.map((event) => (
              <Flex key={event.title} as="li" aria-label={event.title}>
                <Event {...event} />
              </Flex>
            ))}
          </SimpleGrid>
        </Section>
      )}

      {pastEvents.length > 0 && (
        <Section>
          <TextTitle
            size="lg"
            title="Past events"
            py={{ base: "bigger", md: "biggest" }}
            maxW="100%"
            borderTop="1px"
            borderColor="brand.border.light"
          />
          <SimpleGrid as="ol" listStyleType="none" columns={{ base: 1, md: 2, lg: 3 }} gap={10}>
            {pastEvents.map((event) => (
              <Flex key={event.title} as="li" aria-label={event.title}>
                <Event isPast {...event} />
              </Flex>
            ))}
          </SimpleGrid>
        </Section>
      )}
    </Page>
  );
};

const Event: FC<Readonly<EventCard>> = ({
  _type,
  title,
  imageUrl,
  metaImageUrl,
  location,
  startDate,
  endDate,
  excerpt,
  registrationUrl,
  slug,
  isPast = false,
}) => {
  const href = _type === "event" ? registrationUrl : `/events/${slug ?? ""}`;
  const upcomingEventActionLabel = _type === "eventSeries" ? "Find an event near you" : "Register now";

  const bulletSection = (
    <Flex direction="column" gap="tiny" color="brand.textSecondary.light" textStyle="caption2" mt="auto" pt="tiny">
      {location && (
        <Flex align="center" gap="tinier">
          <PinIcon boxSize="icon.xs" />
          <Text>{location}</Text>
        </Flex>
      )}

      <Flex align="center" gap="tinier">
        <CalendarIcon boxSize="icon.xs" />
        <Text>
          {formatDateWithYear(startDate)}
          {endDate && endDate !== startDate && ` - ${formatDateWithYear(endDate)}`}
        </Text>
      </Flex>
    </Flex>
  );

  return (
    <ResourceCard
      title={title}
      titleAs="h3"
      href={href}
      excerpt={excerpt}
      image={
        <Box width="100%" bg="brand.backgroundSecondary.light" display={["none", "block"]}>
          <AspectRatio ratio={2 / 1} width="100%">
            <NextImage src={metaImageUrl ?? imageUrl + "?w=800"} alt={title} layout="fill" objectFit="cover" />
          </AspectRatio>
        </Box>
      }
    >
      {bulletSection}
    </ResourceCard>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [
    upcomingExternalEvents,
    upcomingEventsPages,
    upcomingEventSeries,
    pastExternalEvents,
    pastEventPages,
    pastEventSeries,
    page,
    announcementBanner,
  ] = await Promise.all([
    getUpcomingExternalEvents(),
    getUpcomingEventsPages(),
    getUpcomingEventSeries(),
    getPastExternalEvents(),
    getPastEventsPages(),
    getPastEventSeries(),
    getPage("/events"),
    getAnnouncementBanner(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  type EventType = EventsPageSimple | ExternalEvent | EventSeriesSimple;

  const upcomingEvents = [...upcomingEventsPages, ...upcomingExternalEvents, ...upcomingEventSeries]
    .sort((a: EventType, b: EventType) => {
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    })
    .reverse();

  const pastEvents = [...pastEventPages, ...pastExternalEvents, ...pastEventSeries].sort(
    (a: EventType, b: EventType) => {
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    },
  );

  return {
    props: {
      upcomingEvents,
      pastEvents,
      page,
      announcementBanner,
    },
    revalidate: 60,
  };
};

export default Events;
